import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

// Auto-refresh on 401: try the refresh endpoint once, then retry the original
// request. Stops infinite loops by tagging the retry.
let refreshing = null;
client.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { config, response } = error;
    if (!response || response.status !== 401 || !config || config._retry) {
      return Promise.reject(error);
    }
    // Don't try to refresh the refresh endpoint itself or auth/me on first
    // app load (no cookies present yet) — those legitimately 401.
    if (config.url && (config.url.includes("/auth/refresh") || config.url.includes("/auth/login"))) {
      return Promise.reject(error);
    }
    config._retry = true;
    try {
      refreshing = refreshing || client.post("/auth/refresh");
      await refreshing;
      refreshing = null;
      return client.request(config);
    } catch (refreshErr) {
      refreshing = null;
      return Promise.reject(error);
    }
  },
);

export const api = {
  listReports: () => client.get("/reports").then((r) => r.data),
  getReport: (id) => client.get(`/reports/${id}`).then((r) => r.data),
  createReport: (payload) =>
    client.post("/reports", payload).then((r) => r.data),
  deleteReport: (id) => client.delete(`/reports/${id}`).then((r) => r.data),
  uploadFiles: (id, kind, files) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    return client
      .post(`/reports/${id}/upload?kind=${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  reconcile: (id) =>
    client.post(`/reports/${id}/reconcile`).then((r) => r.data),
  listItems: (id, filter = "all", range_from, range_to) =>
    client
      .get(`/reports/${id}/items`, {
        params: {
          filter,
          ...(range_from && range_to ? { range_from, range_to } : {}),
        },
      })
      .then((r) => r.data),
  getTracking: (id, tracking) =>
    client
      .get(`/reports/${id}/tracking/${tracking}`)
      .then((r) => r.data),
  updateNote: (id, tracking, user_note) =>
    client
      .patch(`/reports/${id}/items/${tracking}`, { user_note })
      .then((r) => r.data),
  disputeDraft: (id, tracking_numbers) =>
    client
      .post(`/reports/${id}/dispute-draft`, { tracking_numbers })
      .then((r) => r.data),
  listProcessedFiles: (id) =>
    client.get(`/reports/${id}/files`).then((r) => r.data),
  reprocessFile: (id, filename, kind) =>
    client
      .post(`/reports/${id}/files/reprocess`, { filename, kind })
      .then((r) => r.data),
  periodSummary: (id, from_, to_) =>
    client
      .get(`/reports/${id}/period-summary`, {
        params: from_ && to_ ? { range_from: from_, range_to: to_ } : {},
      })
      .then((r) => r.data),
  coverage: (id) =>
    client.get(`/reports/${id}/coverage`).then((r) => r.data),
  upsLines: (id, bucket, range_from, range_to) =>
    client
      .get(`/reports/${id}/ups-lines`, {
        params: { bucket, ...(range_from && range_to ? { range_from, range_to } : {}) },
      })
      .then((r) => r.data),
  clearReportData: (id, confirm) =>
    client
      .post(`/reports/${id}/clear-data`, { confirm })
      .then((r) => r.data),
  disableAccount: (confirm) =>
    client
      .post(`/auth/disable-account`, { confirm })
      .then((r) => r.data),
  // Admin
  listUsers: () => client.get("/admin/users").then((r) => r.data),
  createUser: (payload) =>
    client.post("/admin/users", payload).then((r) => r.data),
  patchUser: (id, payload) =>
    client.patch(`/admin/users/${id}`, payload).then((r) => r.data),
  deleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),
};
